import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, Control } from 'react-hook-form';
import { useSelector } from 'react-redux';
import modalStyles from './BeneficiariesUpdateModal.module.scss';
import { RootState } from 'redux/store';
import { AddressDadataReg } from 'components/AddressDadataReg';
import PhoneForm from 'components/PhoneForm';
import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { Button } from 'ui/Button';
import { useUpdateBeneficiarMutation } from 'redux/Api/beneficiariesApi/beneficiariesApi';
import { DaDataAddress, DaDataSuggestion } from 'react-dadata';

interface ModalUpdateCastomerProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export default function ModalUpdateCastomer({
  id,
  isOpen,
  onClose,
  refetch,
}: ModalUpdateCastomerProps) {
  const { infoBeneficiar } = useSelector((state: RootState) => state.beneficiarSlice);
  const [pendingFormData, setPendingFormData] = useState<{
    email: string;
    phone: string;
    jur_name: string;
    jur_address: string;
    inn: string;
    kpp: string;
    ogrn: string;
    name: string;
    patronymic: string;
    surname: string;
  } | null>(null);
  const [confirmSaveCustomerOpen, setConfirmSaveCustomerOpen] = useState(false);
  const [updateBeneficiar, { isLoading: isUpdating }] = useUpdateBeneficiarMutation();
  const [addressValue, setAddressValue] = useState<
    DaDataSuggestion<DaDataAddress> | undefined
  >(
    infoBeneficiar?.jur_address
      ? ({ value: infoBeneficiar.jur_address } as DaDataSuggestion<DaDataAddress>)
      : undefined,
  );
  const refPhone = useRef<HTMLInputElement>(null);
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState<string>('');

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      inn: infoBeneficiar?.inn || '',
      kpp: infoBeneficiar?.kpp || '',
      ogrn: infoBeneficiar?.ogrn || '',
      jur_name: infoBeneficiar?.jur_name || '',
      jur_address: infoBeneficiar?.jur_address || '',
      phone: infoBeneficiar?.phone || '',
      email: infoBeneficiar?.email || '',
      name: infoBeneficiar?.director?.fio?.split(' ')[1] || '',
      surname: infoBeneficiar?.director?.fio?.split(' ')[0] || '',
      patronymic: infoBeneficiar?.director?.fio?.split(' ')[2] || '',
    },
  });

  const onSubmit = (formData: {
    email: string;
    phone: string;
    jur_name: string;
    jur_address: string;
    inn: string;
    kpp: string;
    ogrn: string;
    name: string;
    patronymic: string;
    surname: string;
  }) => {
    setPendingFormData(formData);
    setConfirmSaveCustomerOpen(true);
  };

  useEffect(() => {
    if (infoBeneficiar && isOpen) {
      setValue('inn', infoBeneficiar?.inn || '');
      setValue('kpp', infoBeneficiar?.kpp || '');
      setValue('ogrn', infoBeneficiar?.ogrn || '');
      setValue('jur_name', infoBeneficiar?.jur_name || '');
      const rawPhone = (infoBeneficiar?.phone || '').replace(/\D/g, '');
      const phoneValue =
        rawPhone.startsWith('7') && rawPhone.length === 11 ? rawPhone.slice(1) : rawPhone;
      setValue('phone', phoneValue);
      setValue('email', infoBeneficiar?.email || '');
      setValue('name', infoBeneficiar?.director?.fio?.split(' ')[1] || '');
      setValue('surname', infoBeneficiar?.director?.fio?.split(' ')[0] || '');
      setValue('patronymic', infoBeneficiar?.director?.fio?.split(' ')[2] || '');
      setAddressValue(
        infoBeneficiar?.jur_address
          ? ({ value: infoBeneficiar.jur_address } as DaDataSuggestion<DaDataAddress>)
          : undefined,
      );
      setShowError(false);
      setErrorText('');
    }
  }, [infoBeneficiar, isOpen, setValue]);

  const confirmSaveCustomer = async () => {
    if (!id || !pendingFormData) return;

    const expectedPassword = process.env.REACT_APP_PASSWORD_UPDATE_PROFILE || '';

    const enteredPassword = window.prompt('Введите пароль для подтверждения изменений');

    if (enteredPassword === null) {
      return;
    }

    if (enteredPassword !== expectedPassword) {
      window.alert('Неверный пароль');
      return;
    }

    try {
      const currentJurAddress =
        pendingFormData.jur_address ||
        addressValue?.value ||
        infoBeneficiar?.jur_address ||
        '';

      let normalizedPhone = pendingFormData.phone
        ? pendingFormData.phone.replace(/\D/g, '')
        : '';
      if (normalizedPhone && !normalizedPhone.startsWith('7')) {
        normalizedPhone = '7' + normalizedPhone;
      }

      const body = {
        email: pendingFormData.email || '',
        phone: normalizedPhone || '',
        jur_name: pendingFormData.jur_name || '',
        jur_address: currentJurAddress,
        inn: pendingFormData.inn || '',
        kpp: pendingFormData.kpp ? String(pendingFormData.kpp) : null,
        ogrn: String(pendingFormData.ogrn || '').trim(),
        name: pendingFormData.name || '',
        patronymic: pendingFormData.patronymic || '',
        surname: pendingFormData.surname || '',
      };

      await updateBeneficiar({ id, body }).unwrap();
      await refetch();
      onClose();
      setConfirmSaveCustomerOpen(false);
      setPendingFormData(null);
    } catch (error: unknown) {
      const apiError = error as { data?: { message?: string } };
      setConfirmSaveCustomerOpen(false);
      setErrorText(apiError?.data?.message || 'Ошибка при обновлении данных заказчика');
      setShowError(true);
    }
  };

  const handleClose = () => {
    setPendingFormData(null);
    setConfirmSaveCustomerOpen(false);
    setShowError(false);
    setErrorText('');
    onClose();
  };

  return (
    <>
      {isOpen && (
        <Modal setOpen={handleClose}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Изменение данных Заказчика' />
            <form autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
              <div style={{ display: 'flex', columnGap: '12px' }}>
                <div className={modalStyles.rootInput}>
                  <input
                    className={modalStyles.input}
                    type='number'
                    {...register('inn')}
                  />
                  <span className={modalStyles.rootInput_placeholder}>ИНН</span>
                  {errors.inn && (
                    <p className={modalStyles.errors}>{errors.inn?.message as string}</p>
                  )}
                </div>
                <div className={modalStyles.rootInput}>
                  <input
                    className={modalStyles.input}
                    type='number'
                    {...register('kpp')}
                  />
                  <span className={modalStyles.rootInput_placeholder}>КПП</span>
                  {errors.kpp && (
                    <p className={modalStyles.errors}>{errors.kpp?.message as string}</p>
                  )}
                </div>
              </div>

              <div className={modalStyles.rootInput}>
                <input className={modalStyles.input} type='text' {...register('ogrn')} />
                <span className={modalStyles.rootInput_placeholder}>ОГРН</span>
                {errors.ogrn && (
                  <p className={modalStyles.errors}>{errors.ogrn?.message as string}</p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='text'
                  {...register('jur_name', {
                    required: 'Поле обязательно',
                    minLength: {
                      value: 4,
                      message: 'Минимум 4 символа',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>Наименование</span>
                {errors.jur_name && (
                  <p className={modalStyles.errors}>
                    {errors.jur_name?.message as string}
                  </p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <Controller
                  control={control}
                  name='jur_address'
                  render={({ field }) => (
                    <AddressDadataReg
                      placeholder='Юридический адрес компании'
                      objKey='jur_address'
                      value={addressValue}
                      onChange={(suggestion) => {
                        const address = suggestion?.value || '';
                        field.onChange(address);
                        setAddressValue(suggestion);
                      }}
                    />
                  )}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Юридический адрес компании
                </span>
              </div>

              <div className={modalStyles.rootInput} style={{ position: 'relative' }}>
                <PhoneForm
                  control={control as unknown as Control<Record<string, unknown>>}
                  name='phone'
                  rules={{
                    required: 'Поле обязательно',
                    minLength: {
                      value: 10,
                      message: 'Некорректный номер телефона',
                    },
                  }}
                  error={errors.phone?.message as string}
                  inputClassName={modalStyles.input}
                  inputRef={refPhone}
                  placeholder='+7 (000) 000-00-00'
                />
                <span className={modalStyles.rootInput_placeholder}>Телефон</span>
                {errors.phone && (
                  <p className={modalStyles.errors}>{errors.phone?.message as string}</p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='email'
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Недопустимый формат',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>E-mail</span>
                {errors.email && (
                  <p className={modalStyles.errors}>{errors.email?.message as string}</p>
                )}
              </div>

              <div style={{ display: 'flex', columnGap: '12px' }}>
                <div className={modalStyles.rootInput}>
                  <input
                    className={modalStyles.input}
                    type='text'
                    {...register('surname', {
                      required: 'Поле обязательно',
                      maxLength: {
                        value: 50,
                        message: 'Максимум 50 символов',
                      },
                    })}
                    maxLength={50}
                  />
                  <span className={modalStyles.rootInput_placeholder}>Фамилия</span>
                  {errors.surname && (
                    <p className={modalStyles.errors}>
                      {errors.surname?.message as string}
                    </p>
                  )}
                </div>
                <div className={modalStyles.rootInput}>
                  <input
                    className={modalStyles.input}
                    type='text'
                    {...register('name', {
                      required: 'Поле обязательно',
                      maxLength: {
                        value: 50,
                        message: 'Максимум 50 символов',
                      },
                    })}
                    maxLength={50}
                  />
                  <span className={modalStyles.rootInput_placeholder}>Имя</span>
                  {errors.name && (
                    <p className={modalStyles.errors}>{errors.name?.message as string}</p>
                  )}
                </div>
                <div className={modalStyles.rootInput}>
                  <input
                    className={modalStyles.input}
                    type='text'
                    {...register('patronymic', {
                      maxLength: {
                        value: 50,
                        message: 'Максимум 50 символов',
                      },
                    })}
                    maxLength={50}
                  />
                  <span className={modalStyles.rootInput_placeholder}>Отчество</span>
                  {errors.patronymic && (
                    <p className={modalStyles.errors}>
                      {errors.patronymic?.message as string}
                    </p>
                  )}
                </div>
              </div>
              {showError && <p style={{ color: 'red' }}>{errorText}</p>}
              <div className={modalStyles.buttons}>
                <Button
                  onClick={handleClose}
                  className={modalStyles.button_cancel}
                  theme='outline'>
                  Отмена
                </Button>
                <Button
                  disabled={isUpdating}
                  loading={isUpdating}
                  onClick={handleSubmit(onSubmit)}
                  className={modalStyles.button}
                  theme='filed'>
                  Сохранить
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {confirmSaveCustomerOpen && (
        <Modal setOpen={setConfirmSaveCustomerOpen}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Подтверждение сохранения' />
            <p style={{ marginBottom: '20px', fontSize: '14px' }}>
              Вы уверены, что хотите сохранить изменения данных заказчика?
            </p>
            <div className={modalStyles.buttons}>
              <Button
                onClick={() => setConfirmSaveCustomerOpen(false)}
                className={modalStyles.button_cancel}
                theme='outline'>
                Отмена
              </Button>
              <Button
                disabled={isUpdating}
                loading={isUpdating}
                onClick={confirmSaveCustomer}
                className={modalStyles.button}
                theme='filed'>
                Подтвердить
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
